<template>
  <AppLayout>
    <div class="flex flex-col min-h-full bg-slate-50 dark:bg-slate-950">

      <!-- Sticky top bar -->
      <div class="sticky top-0 z-20 bg-slate-50 dark:bg-slate-950 px-4 pt-4 pb-0">
        <div class="flex items-center justify-between mb-3">
          <div>
            <h1 class="text-xl font-bold text-slate-800 dark:text-white leading-tight">Profit & Loss</h1>
            <p class="text-xs text-slate-400 mt-0.5">{{ periodLabel }}</p>
          </div>
          <button @click="showFilters = !showFilters"
            class="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl transition"
            :class="showFilters
              ? 'bg-indigo-500 text-white'
              : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'">
            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z"/>
            </svg>
            Filters
          </button>
        </div>

        <div v-if="showFilters"
          class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4 mb-3">
          <div class="grid grid-cols-2 gap-3">
            <div class="flex flex-col gap-1">
              <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Period</label>
              <select v-model="filters.preset"
                class="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                       text-slate-800 dark:text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="this_year">This Year</option>
                <option value="last_year">Last Year</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Group By</label>
              <select v-model="filters.groupBy"
                class="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                       text-slate-800 dark:text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <template v-if="filters.preset === 'custom'">
              <div class="flex flex-col gap-1">
                <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">From</label>
                <input type="date" v-model="filters.from"
                  class="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                         text-slate-800 dark:text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div class="flex flex-col gap-1">
                <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">To</label>
                <input type="date" v-model="filters.to"
                  class="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                         text-slate-800 dark:text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
            </template>
          </div>
        </div>
      </div>

      <div class="flex-1 px-4 pb-6 pt-3 space-y-4">

        <!-- KPI grid -->
        <div class="grid grid-cols-3 lg:grid-cols-6 gap-3">
          <div v-for="kpi in profitKPIs" :key="kpi.label"
            class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-3 lg:p-4">
            <p class="text-[9px] lg:text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">{{ kpi.label }}</p>
            <p class="text-sm lg:text-base font-bold leading-tight" :class="kpi.color">{{ kpi.value }}</p>
            <p v-if="kpi.sub" class="text-[9px] lg:text-[10px] mt-1" :class="kpi.subColor || 'text-slate-400'">{{ kpi.sub }}</p>
          </div>
        </div>

        <!-- Row: Trend chart + Profit by product doughnut -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">

          <!-- Revenue / Cost / Profit multi-line -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <p class="text-sm font-semibold text-slate-700 dark:text-white mb-4">Revenue · Cost · Profit Trend</p>
            <Line :data="trendChartData" :options="lineOptions" style="max-height:200px" />
          </div>

          <!-- Profit by product doughnut -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <p class="text-sm font-semibold text-slate-700 dark:text-white mb-4">Profit Share by Product</p>
            <div class="flex items-center gap-4">
              <div class="shrink-0">
                <Doughnut :data="profitShareData" :options="doughnutOptions" style="width:130px;height:130px" />
              </div>
              <div class="flex-1 space-y-2">
                <div v-for="item in profitShareLegend" :key="item.label" class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span class="w-2 h-2 rounded-full shrink-0" :style="{ backgroundColor: item.color }"></span>
                    <span class="text-[10px] text-slate-500 dark:text-slate-400 truncate">{{ item.label }}</span>
                  </div>
                  <span class="text-[10px] font-bold text-slate-700 dark:text-slate-300 ml-2 shrink-0">{{ item.pct }}%</span>
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- Row: Best/worst month highlights -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div class="bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-100 dark:border-emerald-900 rounded-2xl p-4">
            <p class="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide mb-1">Best Month</p>
            <p class="text-lg font-bold text-emerald-700 dark:text-emerald-300">December</p>
            <p class="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">Rs 29k profit · 38% margin</p>
          </div>
          <div class="bg-red-50 dark:bg-red-950/40 border border-red-100 dark:border-red-900 rounded-2xl p-4">
            <p class="text-[10px] font-semibold text-red-500 dark:text-red-400 uppercase tracking-wide mb-1">Worst Month</p>
            <p class="text-lg font-bold text-red-600 dark:text-red-300">February</p>
            <p class="text-xs text-red-500 dark:text-red-400 mt-0.5">Rs 13k profit · 25% margin</p>
          </div>
          <div class="bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900 rounded-2xl p-4">
            <p class="text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wide mb-1">Avg Monthly</p>
            <p class="text-lg font-bold text-indigo-700 dark:text-indigo-300">Rs 21.1k</p>
            <p class="text-xs text-indigo-500 dark:text-indigo-400 mt-0.5">35.7% avg margin</p>
          </div>
        </div>

        <!-- Monthly P&L breakdown -->
        <div>
          <p class="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Monthly Breakdown</p>
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div v-for="row in monthlyPL" :key="row.month"
              class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
              <!-- Header -->
              <div class="flex items-center justify-between mb-3">
                <div>
                  <p class="font-semibold text-slate-800 dark:text-white text-sm">{{ row.month }}</p>
                  <p class="text-[10px] text-slate-400 mt-0.5">{{ row.invoices }} invoices</p>
                </div>
                <div class="text-right">
                  <span class="text-sm font-bold px-2.5 py-1 rounded-xl block"
                    :class="row.profit > 0 ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400' : 'bg-red-50 dark:bg-red-950 text-red-500 dark:text-red-400'">
                    {{ shortNum(row.profit) }}
                  </span>
                  <!-- MoM change -->
                  <p class="text-[10px] mt-1 font-semibold"
                    :class="row.mom > 0 ? 'text-emerald-500' : row.mom < 0 ? 'text-red-400' : 'text-slate-400'">
                    {{ row.mom > 0 ? '↑' : row.mom < 0 ? '↓' : '—' }} {{ Math.abs(row.mom) }}% MoM
                  </p>
                </div>
              </div>
              <!-- Stats -->
              <div class="grid grid-cols-3 gap-2 text-center">
                <div class="bg-emerald-50 dark:bg-emerald-950 rounded-xl p-2">
                  <p class="text-[9px] text-slate-400 mb-0.5">Revenue</p>
                  <p class="text-xs font-bold text-emerald-600 dark:text-emerald-400">{{ shortNum(row.revenue) }}</p>
                </div>
                <div class="bg-red-50 dark:bg-red-950 rounded-xl p-2">
                  <p class="text-[9px] text-slate-400 mb-0.5">Cost</p>
                  <p class="text-xs font-bold text-red-500 dark:text-red-400">{{ shortNum(row.cost) }}</p>
                </div>
                <div class="rounded-xl p-2" :class="row.margin >= 30 ? 'bg-indigo-50 dark:bg-indigo-950' : 'bg-amber-50 dark:bg-amber-950'">
                  <p class="text-[9px] text-slate-400 mb-0.5">Margin</p>
                  <p class="text-xs font-bold" :class="row.margin >= 30 ? 'text-indigo-600 dark:text-indigo-400' : 'text-amber-500'">{{ row.margin }}%</p>
                </div>
              </div>
              <!-- Margin bar -->
              <div class="mt-3">
                <div class="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div class="h-full rounded-full transition-all"
                    :class="row.margin >= 30 ? 'bg-emerald-500' : 'bg-amber-400'"
                    :style="{ width: Math.min(row.margin, 100) + '%' }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Profit by product -->
        <div>
          <p class="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Profit by Product</p>
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div v-for="row in productPLData" :key="row.name"
              class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
              <!-- Header -->
              <div class="flex items-start justify-between mb-3">
                <p class="font-semibold text-slate-800 dark:text-white text-sm leading-snug max-w-[65%]">{{ row.name }}</p>
                <span class="text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ml-2"
                  :class="row.margin >= 30 ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400'
                    : row.margin >= 15 ? 'bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400'
                    : 'bg-red-50 dark:bg-red-950 text-red-500 dark:text-red-400'">
                  {{ row.margin }}% margin
                </span>
              </div>
              <!-- Stats -->
              <div class="grid grid-cols-4 gap-1.5 text-center mb-3">
                <div class="bg-slate-50 dark:bg-slate-800 rounded-xl p-2">
                  <p class="text-[9px] text-slate-400 mb-0.5">Qty</p>
                  <p class="text-[11px] font-bold text-slate-700 dark:text-slate-300">{{ row.qty }}</p>
                </div>
                <div class="bg-emerald-50 dark:bg-emerald-950 rounded-xl p-2">
                  <p class="text-[9px] text-slate-400 mb-0.5">Revenue</p>
                  <p class="text-[11px] font-bold text-emerald-600 dark:text-emerald-400">{{ shortNum(row.revenue) }}</p>
                </div>
                <div class="bg-red-50 dark:bg-red-950 rounded-xl p-2">
                  <p class="text-[9px] text-slate-400 mb-0.5">Cost</p>
                  <p class="text-[11px] font-bold text-red-500 dark:text-red-400">{{ shortNum(row.cost) }}</p>
                </div>
                <div class="bg-violet-50 dark:bg-violet-950 rounded-xl p-2">
                  <p class="text-[9px] text-slate-400 mb-0.5">Profit</p>
                  <p class="text-[11px] font-bold text-violet-600 dark:text-violet-400">{{ shortNum(row.profit) }}</p>
                </div>
              </div>
              <!-- Margin bar -->
              <div class="flex items-center gap-2">
                <div class="flex-1 h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div class="h-full rounded-full"
                    :class="row.margin >= 30 ? 'bg-emerald-500' : row.margin >= 15 ? 'bg-amber-400' : 'bg-red-500'"
                    :style="{ width: Math.min(row.margin, 100) + '%' }"></div>
                </div>
                <span class="text-[10px] text-slate-400 shrink-0">{{ row.margin }}%</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </AppLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { Line, Doughnut } from 'vue-chartjs'
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, ArcElement } from 'chart.js'
import AppLayout from '@/layouts/AppLayout.vue'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, ArcElement)

const page = usePage()
const businessName = computed(() => page.props.auth?.user?.business_name?.trim() || 'Master Ledger')
onMounted(() => { document.title = `Profit & Loss | ${businessName.value}` })

const showFilters = ref(false)
const filters = ref({ preset: 'this_year', groupBy: 'monthly', from: '', to: '' })
const periodLabel = computed(() => {
  const map = { this_month: 'This Month', last_month: 'Last Month', this_year: 'This Year', last_year: 'Last Year', custom: 'Custom Range' }
  return map[filters.value.preset] || 'Custom Range'
})

function shortNum(n) {
  n = Number(n) || 0
  if (n >= 100000) return 'Rs ' + (n / 100000).toFixed(1) + 'L'
  if (n >= 1000)   return 'Rs ' + (n / 1000).toFixed(1) + 'k'
  return 'Rs ' + n
}

const tickColor = '#94a3b8'
const gridColor = 'rgba(148,163,184,0.12)'
const baseFont  = { family: 'inherit', size: 10 }

// ── KPIs ─────────────────────────────────────────────
const profitKPIs = [
  { label: 'Revenue',       value: 'Rs 4.85L', color: 'text-emerald-600 dark:text-emerald-400', sub: '48 invoices' },
  { label: 'Cost of Goods', value: 'Rs 3.12L', color: 'text-red-500 dark:text-red-400',         sub: 'total COGS' },
  { label: 'Gross Profit',  value: 'Rs 1.73L', color: 'text-violet-600 dark:text-violet-400',   sub: '35.7% margin', subColor: 'text-emerald-500' },
  { label: 'Best Month',    value: 'Dec',       color: 'text-slate-800 dark:text-white',          sub: 'Rs 29k profit' },
  { label: 'Worst Month',   value: 'Feb',       color: 'text-slate-800 dark:text-white',          sub: 'Rs 13k profit' },
  { label: 'Avg Monthly',   value: 'Rs 21.1k',  color: 'text-indigo-600 dark:text-indigo-400',   sub: 'per month' },
]

// ── Multi-line trend chart ────────────────────────────
const lineOptions = {
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { labels: { color: tickColor, font: baseFont, boxWidth: 10, padding: 12 } }, tooltip: { mode: 'index' } },
  scales: {
    x: { grid: { display: false }, ticks: { color: tickColor, font: baseFont } },
    y: { grid: { color: gridColor }, ticks: { color: tickColor, font: baseFont, callback: v => (v / 1000) + 'k' } },
  },
  elements: { line: { tension: 0.4 }, point: { radius: 2, hoverRadius: 5 } },
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const revenueData = [55000, 52000, 68000, 75000, 61000, 83000, 77000, 69000, 92000, 88000, 81000, 94000]
const costData    = [41000, 39000, 49000, 54000, 44000, 59000, 54000, 49000, 64000, 63000, 57000, 65000]
const profitData  = revenueData.map((r, i) => r - costData[i])

const trendChartData = {
  labels: MONTHS,
  datasets: [
    { label: 'Revenue', data: revenueData, borderColor: 'rgba(16,185,129,1)',   backgroundColor: 'rgba(16,185,129,0.06)', fill: false },
    { label: 'Cost',    data: costData,    borderColor: 'rgba(239,68,68,1)',    backgroundColor: 'rgba(239,68,68,0.06)',  fill: false },
    { label: 'Profit',  data: profitData,  borderColor: 'rgba(139,92,246,1)',   backgroundColor: 'rgba(139,92,246,0.08)', fill: true  },
  ],
}

// ── Profit share doughnut ─────────────────────────────
const doughnutOptions = {
  responsive: false,
  plugins: { legend: { display: false } },
  cutout: '70%',
}
const DOUGHNUT_COLORS = ['rgba(16,185,129,0.85)', 'rgba(99,102,241,0.85)', 'rgba(251,191,36,0.85)', 'rgba(139,92,246,0.85)', 'rgba(239,68,68,0.85)', 'rgba(148,163,184,0.6)']
const profitShareData = {
  labels: ['Cement', 'Steel Rod', 'PVC Pipe', 'GI Sheet', 'Wire', 'Others'],
  datasets: [{ data: [29400, 21600, 18600, 17100, 10500, 7500], backgroundColor: DOUGHNUT_COLORS, borderWidth: 0 }],
}
const profitShareLegend = [
  { label: 'Cement 50kg',  pct: 28, color: DOUGHNUT_COLORS[0] },
  { label: 'Steel Rod',    pct: 21, color: DOUGHNUT_COLORS[1] },
  { label: 'PVC Pipe',     pct: 18, color: DOUGHNUT_COLORS[2] },
  { label: 'GI Sheet',     pct: 16, color: DOUGHNUT_COLORS[3] },
  { label: 'Wire 2.5mm',   pct: 10, color: DOUGHNUT_COLORS[4] },
  { label: 'Others',       pct: 7,  color: DOUGHNUT_COLORS[5] },
]

// ── Monthly P&L ───────────────────────────────────────
const monthlyPL = [
  { month: 'Dec 2025', invoices: 6,  revenue: 94000, cost: 65000, profit: 29000, margin: 31, mom: 20  },
  { month: 'Nov 2025', invoices: 5,  revenue: 81000, cost: 57000, profit: 24000, margin: 30, mom: -4  },
  { month: 'Oct 2025', invoices: 7,  revenue: 88000, cost: 63000, profit: 25000, margin: 28, mom: -11 },
  { month: 'Sep 2025', invoices: 6,  revenue: 92000, cost: 64000, profit: 28000, margin: 30, mom: 40  },
  { month: 'Aug 2025', invoices: 4,  revenue: 69000, cost: 49000, profit: 20000, margin: 29, mom: -13 },
  { month: 'Jul 2025', invoices: 5,  revenue: 77000, cost: 54000, profit: 23000, margin: 30, mom: -4  },
  { month: 'Jun 2025', invoices: 6,  revenue: 83000, cost: 59000, profit: 24000, margin: 29, mom: 41  },
  { month: 'May 2025', invoices: 4,  revenue: 61000, cost: 44000, profit: 17000, margin: 28, mom: -19 },
  { month: 'Apr 2025', invoices: 5,  revenue: 75000, cost: 54000, profit: 21000, margin: 28, mom: 10  },
  { month: 'Mar 2025', invoices: 4,  revenue: 68000, cost: 49000, profit: 19000, margin: 28, mom: 46  },
  { month: 'Feb 2025', invoices: 3,  revenue: 52000, cost: 39000, profit: 13000, margin: 25, mom: -7  },
  { month: 'Jan 2025', invoices: 4,  revenue: 55000, cost: 41000, profit: 14000, margin: 25, mom: 0   },
]

// ── Product P&L ───────────────────────────────────────
const productPLData = [
  { name: 'Cement 50kg (OPC)',  qty: 420, revenue: 84000, cost: 54600, profit: 29400, margin: 35 },
  { name: 'Steel Rod 12mm',     qty: 180, revenue: 72000, cost: 50400, profit: 21600, margin: 30 },
  { name: 'PVC Pipe 1 inch',    qty: 310, revenue: 46500, cost: 27900, profit: 18600, margin: 40 },
  { name: 'GI Sheet 20 gauge',  qty: 95,  revenue: 57000, cost: 39900, profit: 17100, margin: 30 },
  { name: 'Wire 2.5mm',         qty: 500, revenue: 35000, cost: 24500, profit: 10500, margin: 30 },
  { name: 'Emulsion Paint 4L',  qty: 75,  revenue: 30000, cost: 22500, profit: 7500,  margin: 25 },
  { name: 'GI Pipe 1 inch',     qty: 60,  revenue: 28000, cost: 23240, profit: 4760,  margin: 17 },
  { name: 'Brick (Standard)',   qty: 200, revenue: 24000, cost: 21600, profit: 2400,  margin: 10 },
]
</script>

<style scoped>
.scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
.scrollbar-hide::-webkit-scrollbar { display: none; }
</style>
